import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  CalendarDays,
  Download,
  FileSpreadsheet,
  FileText,
  Filter,
  Gauge,
  History as HistoryIcon,
  Info,
  Loader2,
  Play,
  Printer,
  RefreshCw,
  RotateCcw,
  ShieldCheck,
  Table2,
} from "lucide-react";
import {
  HrBtn,
  HrSelect,
  HrInput,
} from "../../../components/hr/HrDesignSystem";
import ReportsEnterpriseShell from "../components/ReportsEnterpriseShell";
import { useReportsNavigation } from "../useReportsNavigation";
import { PAYROLL_HUB_REPORTS } from "../PayrollReportsWorkspace";
import {
  getCatalog,
  previewReport,
  downloadReport,
  getHistory,
  getSchedules,
  createSchedule,
  toggleSchedule,
  deleteSchedule,
  downloadArtifact,
  getComplianceScore,
  triggerBlobDownload,
} from "./statutoryReportsApi";
import { getApiErrorDetail } from "../../../utils/idempotency";
import { printRowsAsTable } from "../reportPrint";
import "../../../styles/payrollReportsWorkspace.css";
import "../../../styles/statutoryReports.css";

const MODES = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "half_yearly", label: "Half-Yearly" },
  { value: "financial_year", label: "Financial Year" },
  { value: "custom", label: "Custom Range" },
];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function formatMoney(v) {
  if (v == null || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function cellValue(row, col) {
  const raw = row[col.key];
  if (raw == null || raw === "") return col.type === "money" ? "" : "—";
  if (col.type === "money") return `₹${formatMoney(raw)}`;
  return String(raw);
}

/** One-line explanation shown under the tab bar for the selected tab. */
const TAB_INFO = {
  preview:
    "Preview: the report rows and totals for the selected period, read from finalized (processed) payroll runs.",
  exceptions:
    "Exceptions: data problems found while building the report (e.g. missing UAN / ESI number, wage above ceiling) that can cause filing errors. Fix them before filing.",
  history:
    "Download History: every file downloaded for this report, with who and when. Re-download the exact same file any time.",
  schedules:
    "Scheduler: generate this report automatically (monthly / quarterly / yearly) and email it to the recipients you enter.",
  compliance:
    "Compliance Score: an overall 0\u2013100 health score for statutory compliance (registrations, employee IDs, filings), with what to fix.",
};

const DOWNLOAD_BUTTONS = [
  { fmt: "xlsx", label: "Excel", cls: "prw__btn--excel", Icon: FileSpreadsheet },
  { fmt: "pdf", label: "PDF", cls: "prw__btn--pdf", Icon: FileText },
  { fmt: "csv", label: "CSV", cls: "prw__btn--csv", Icon: Download },
];

function titleCase(key) {
  return String(key).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function periodLabel(mode, period) {
  const fy = Number(period.financial_year);
  const fyText = `FY ${fy}\u2013${String(fy + 1).slice(-2)}`;
  if (mode === "monthly") return `${MONTHS[Number(period.month) - 1] || ""} ${period.year}`;
  if (mode === "quarterly") return `Q${period.quarter} ${fyText}`;
  if (mode === "half_yearly") return `H${period.half} ${fyText}`;
  if (mode === "financial_year") return fyText;
  if (mode === "custom") return `${period.start_date || "\u2014"} \u2192 ${period.end_date || "\u2014"}`;
  return "\u2014";
}

const nowYear = new Date().getFullYear();
const currentFyStart = new Date().getMonth() >= 3 ? nowYear : nowYear - 1;

export default function StatutoryReportsWorkspace() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [theme, setTheme] = useState("light");
  const nav = useReportsNavigation({ payrollReports: PAYROLL_HUB_REPORTS });
  const [catalog, setCatalog] = useState([]);
  const selectedKey = searchParams.get("report") || "";
  const [mode, setMode] = useState("monthly");
  const [period, setPeriod] = useState({
    year: nowYear,
    month: new Date().getMonth() + 1,
    financial_year: currentFyStart,
    quarter: 1,
    half: 1,
    start_date: "",
    end_date: "",
  });
  const [generatedAt, setGeneratedAt] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [tab, setTab] = useState("preview");

  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [error, setError] = useState(null);

  const [history, setHistory] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [cadences, setCadences] = useState(["monthly", "quarterly", "yearly"]);
  const [compliance, setCompliance] = useState(null);

  const selected = useMemo(
    () => catalog.find((r) => r.report_key === selectedKey),
    [catalog, selectedKey]
  );

  useEffect(() => {
    getCatalog()
      .then((res) => {
        const items = res.reports || [];
        setCatalog(items);
        if (!searchParams.get("report") && items.length) {
          setSearchParams({ report: items[0].report_key }, { replace: true });
        }
      })
      .catch((e) => setError(getApiErrorDetail(e) || e.message));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedKey === "esi_monthly") {
      navigate("/reports/esi", { replace: true });
    }
  }, [selectedKey, navigate]);

  // Keep mode valid for the selected report.
  useEffect(() => {
    if (selected && !selected.period_modes.includes(mode)) {
      setMode(selected.period_modes[0]);
    }
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildBody = useCallback(
    (extra = {}) => ({
      report_key: selectedKey,
      mode,
      year: Number(period.year),
      month: Number(period.month),
      quarter: Number(period.quarter),
      half: Number(period.half),
      financial_year: Number(period.financial_year),
      start_date: period.start_date || null,
      end_date: period.end_date || null,
      ...extra,
    }),
    [selectedKey, mode, period]
  );

  const runPreview = useCallback(
    async (goPage = 1) => {
      if (!selectedKey) return;
      setLoading(true);
      setError(null);
      try {
        const res = await previewReport(buildBody(), { page: goPage, pageSize: 50 });
        setData(res);
        setGeneratedAt(new Date());
        setPage(goPage);
        setTab("preview");
      } catch (e) {
        setData(null);
        setError(getApiErrorDetail(e) || e.message);
      } finally {
        setLoading(false);
      }
    },
    [selectedKey, buildBody]
  );

  const runDownload = useCallback(async (format) => {
    if (!selectedKey) return;
    setDownloading(format);
    setError(null);
    try {
      const res = await downloadReport(buildBody({ format }));
      triggerBlobDownload(res, `${selectedKey}.${format}`);
    } catch (e) {
      // Blob error bodies need decoding.
      let msg = e.message;
      if (e?.response?.data instanceof Blob) {
        try {
          const body = JSON.parse(await e.response.data.text());
          msg = getApiErrorDetail({ response: { data: body } }) || msg;
        } catch (_) {} // eslint-disable-line no-empty
      }
      setError(msg);
    } finally {
      setDownloading("");
    }
  }, [selectedKey, buildBody]);

  // Print every row of the period (not just the 50-row preview page) as a clean table.
  const runPrint = useCallback(async () => {
    if (!selectedKey) return;
    setPrinting(true);
    setError(null);
    try {
      const res = await previewReport(buildBody(), { page: 1, pageSize: 500 });
      const cols = res?.columns || [];
      const allRows = res?.rows || [];
      if (!allRows.length) {
        setError("No rows to print for this period.");
        return;
      }
      const labels = cols.map((c) => c.label || c.key);
      printRowsAsTable({
        title: selected?.name || "Statutory Report",
        subtitle: `Period: ${periodLabel(mode, period)}  |  Rows: ${allRows.length}`,
        columns: labels,
        rows: allRows.map((r) => Object.fromEntries(cols.map((c) => [c.label || c.key, cellValue(r, c)]))),
        keyColumns: labels.filter((l) => /^employee (code|name)$/i.test(l)),
      });
    } catch (e) {
      setError(getApiErrorDetail(e) || e.message);
    } finally {
      setPrinting(false);
    }
  }, [selectedKey, buildBody, selected, mode, period]);

  const resetFilters = () => {
    setMode(selected?.period_modes?.[0] || "monthly");
    setPeriod({
      year: nowYear,
      month: new Date().getMonth() + 1,
      financial_year: currentFyStart,
      quarter: 1,
      half: 1,
      start_date: "",
      end_date: "",
    });
    setData(null);
    setGeneratedAt(null);
    setError(null);
  };

  const loadHistory = useCallback(() => {
    getHistory({ reportKey: selectedKey })
      .then((res) => setHistory(res.history || []))
      .catch(() => {});
  }, [selectedKey]);

  const loadSchedules = useCallback(() => {
    getSchedules()
      .then((res) => {
        setSchedules(res.schedules || []);
        if (res.cadences) setCadences(res.cadences);
      })
      .catch(() => {});
  }, []);

  const loadCompliance = useCallback(() => {
    getComplianceScore(buildBody())
      .then(setCompliance)
      .catch((e) => setError(getApiErrorDetail(e) || e.message));
  }, [buildBody]);

  useEffect(() => {
    if (tab === "history") loadHistory();
    if (tab === "schedules") loadSchedules();
    if (tab === "compliance") loadCompliance();
  }, [tab, loadHistory, loadSchedules, loadCompliance]);

  const summary = data?.summary || {};
  const pagination = data?.pagination;
  const columns = data?.columns || [];
  const rows = data?.rows || [];
  const exceptions = data?.exceptions || {};
  const exceptionEntries = Object.entries(exceptions).filter(([, v]) => Array.isArray(v));

  return (
    <div className="prw stat-ws-page" data-theme={theme}>
      <ReportsEnterpriseShell
        categoryOrder={nav.categoryOrder}
        reportsByCategory={nav.reportsByCategory}
        selectedKey={selectedKey}
        selectedReport={nav.selectedReport || selected}
        reportSearch={nav.reportSearch}
        onReportSearchChange={nav.onReportSearchChange}
        favorites={nav.favorites}
        onToggleFavorite={nav.onToggleFavorite}
        recentReports={nav.recentReports}
        favReports={nav.favReports}
        onSelectReport={nav.onSelectReport}
        onThemeChange={setTheme}
        theme={theme}
        moduleTitle="Statutory Reports"
      >
      <div className="prw__body">
        <div className="stat-ws stat-ws--full">
        <main className="stat-ws__main">
          {/* Page header — same layout as the other report pages */}
          <div className="prw__page-header">
            <div className="prw__page-header-main">
              <div className="prw__page-icon">
                <ShieldCheck size={26} />
              </div>
              <div>
                <h1 className="prw__page-title">{selected?.name || "Select a report"}</h1>
                <p className="prw__page-desc">{selected?.description}</p>
                <div className="prw__page-meta">
                  <span className="prw__meta-chip">
                    <CalendarDays size={13} /> Period: <b>{periodLabel(mode, period)}</b>
                  </span>
                  <span className="prw__meta-chip">
                    <HistoryIcon size={13} /> Last generated:{" "}
                    <b>{generatedAt ? generatedAt.toLocaleString() : "\u2014"}</b>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Filter card */}
          <div className="prw__filter-card">
            <p className="prw__filter-card-title">
              <Filter size={16} /> Report Filters
            </p>
            <div className="prw__filter-grid">
              <div className="prw__filter">
                <label htmlFor="st-mode"><CalendarDays size={12} /> Period</label>
                <select id="st-mode" value={mode} onChange={(e) => setMode(e.target.value)}>
                  {MODES.filter((m) => !selected || selected.period_modes.includes(m.value)).map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              {mode === "monthly" && (
                <>
                  <div className="prw__filter">
                    <label htmlFor="st-month"><CalendarDays size={12} /> Month</label>
                    <select id="st-month" value={period.month} onChange={(e) => setPeriod({ ...period, month: e.target.value })}>
                      {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                    </select>
                  </div>
                  <div className="prw__filter">
                    <label htmlFor="st-year"><CalendarDays size={12} /> Year</label>
                    <input id="st-year" type="number" value={period.year} onChange={(e) => setPeriod({ ...period, year: e.target.value })} />
                  </div>
                </>
              )}
              {(mode === "quarterly" || mode === "half_yearly" || mode === "financial_year") && (
                <div className="prw__filter">
                  <label htmlFor="st-fy"><CalendarDays size={12} /> Financial Year (Start Year)</label>
                  <input id="st-fy" type="number" value={period.financial_year} onChange={(e) => setPeriod({ ...period, financial_year: e.target.value })} />
                </div>
              )}
              {mode === "quarterly" && (
                <div className="prw__filter">
                  <label htmlFor="st-q"><CalendarDays size={12} /> Quarter</label>
                  <select id="st-q" value={period.quarter} onChange={(e) => setPeriod({ ...period, quarter: e.target.value })}>
                    {[1, 2, 3, 4].map((q) => <option key={q} value={q}>Q{q}</option>)}
                  </select>
                </div>
              )}
              {mode === "half_yearly" && (
                <div className="prw__filter">
                  <label htmlFor="st-half"><CalendarDays size={12} /> Half</label>
                  <select id="st-half" value={period.half} onChange={(e) => setPeriod({ ...period, half: e.target.value })}>
                    <option value={1}>H1 (Apr–Sep)</option>
                    <option value={2}>H2 (Oct–Mar)</option>
                  </select>
                </div>
              )}
              {mode === "custom" && (
                <>
                  <div className="prw__filter">
                    <label htmlFor="st-from"><CalendarDays size={12} /> From Date</label>
                    <input id="st-from" type="date" value={period.start_date} onChange={(e) => setPeriod({ ...period, start_date: e.target.value })} />
                  </div>
                  <div className="prw__filter">
                    <label htmlFor="st-to"><CalendarDays size={12} /> To Date</label>
                    <input id="st-to" type="date" value={period.end_date} onChange={(e) => setPeriod({ ...period, end_date: e.target.value })} />
                  </div>
                </>
              )}

              <div className="prw__filter-actions">
                <button
                  type="button"
                  className="prw__btn prw__btn--generate"
                  onClick={() => runPreview(1)}
                  disabled={loading || !selectedKey}
                  aria-busy={loading}
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
                  Generate &amp; Preview
                </button>
                <button type="button" className="prw__btn prw__btn--ghost" onClick={resetFilters}>
                  <RotateCcw size={16} /> Reset
                </button>
              </div>
            </div>
          </div>

          {/* Action toolbar */}
          <div className="prw__toolbar">
            <button
              type="button"
              className="prw__btn prw__btn--refresh prw__btn--sm"
              onClick={() => runPreview(page)}
              disabled={loading || !selectedKey}
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            {DOWNLOAD_BUTTONS.filter((b) => (selected?.formats || ["xlsx", "csv", "pdf"]).includes(b.fmt)).map(
              ({ fmt, label, cls, Icon }) => (
                <button
                  key={fmt}
                  type="button"
                  className={`prw__btn ${cls} prw__btn--sm`}
                  onClick={() => runDownload(fmt)}
                  disabled={Boolean(downloading) || !selectedKey}
                >
                  {downloading === fmt ? <Loader2 size={14} className="animate-spin" /> : <Icon size={14} />} {label}
                </button>
              )
            )}
            <button
              type="button"
              className="prw__btn prw__btn--print prw__btn--sm"
              onClick={runPrint}
              disabled={printing || !selectedKey}
            >
              {printing ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />} Print
            </button>
          </div>

          {error && (
            <div className="stat-ws__error">
              <AlertTriangle size={16} /> {error}
            </div>
          )}

          {/* Tabs */}
          <nav className="stat-ws__tabs">
            {[
              ["preview", "Preview", Table2],
              ["exceptions", "Exceptions", AlertTriangle],
              ["history", "Download History", HistoryIcon],
              ["schedules", "Scheduler", CalendarDays],
              ["compliance", "Compliance Score", Gauge],
            ].map(([key, label, Icon]) => (
              <button key={key} className={tab === key ? "is-active" : ""} onClick={() => setTab(key)}>
                <Icon size={14} /> {label}
                {key === "exceptions" && exceptionEntries.length > 0 && (
                  <span className="stat-ws__badge">
                    {exceptionEntries.reduce((a, [, v]) => a + v.length, 0)}
                  </span>
                )}
              </button>
            ))}
          </nav>
          <p className="stat-ws__tab-info">
            <Info size={14} /> {TAB_INFO[tab]}
          </p>

          {/* Panels */}
          <section className="stat-ws__body">
            {tab === "preview" && (
              <>
                {data && (
                  <div className="stat-ws__kpis">
                    {Object.entries(summary)
                      .filter(([, v]) => typeof v !== "object")
                      .slice(0, 8)
                      .map(([k, v]) => (
                        <div key={k} className="stat-ws__kpi">
                          <p className="stat-ws__kpi-label">{titleCase(k)}</p>
                          <p className="stat-ws__kpi-value">
                            {/^total_|_pf$|contribution|gross|wage|amount/.test(k) ? `₹${formatMoney(v)}` : String(v)}
                          </p>
                        </div>
                      ))}
                  </div>
                )}

                {!data && !loading && (
                  <div className="stat-ws__empty">
                    <Table2 size={40} />
                    <p>Select a report and period, then <b>Generate & Preview</b>.</p>
                  </div>
                )}
                {loading && <div className="stat-ws__empty"><Loader2 size={28} className="animate-spin" /></div>}

                {data && (
                  <div className="stat-ws__grid-wrap">
                    <table className="stat-ws__grid">
                      <thead>
                        <tr>{columns.map((c) => <th key={c.key} className={c.type === "money" || c.type === "number" ? "num" : ""}>{c.label}</th>)}</tr>
                      </thead>
                      <tbody>
                        {rows.map((row, i) => (
                          <tr key={row.employee_id || i}>
                            {columns.map((c) => (
                              <td key={c.key} className={c.type === "money" || c.type === "number" ? "num" : ""}>
                                {cellValue(row, c)}
                              </td>
                            ))}
                          </tr>
                        ))}
                        {rows.length === 0 && (
                          <tr><td colSpan={columns.length} className="stat-ws__norows">No rows for this period. Reports read finalized payroll runs only.</td></tr>
                        )}
                      </tbody>
                    </table>
                    {pagination && pagination.total_pages > 1 && (
                      <div className="stat-ws__pager">
                        <HrBtn variant="ghost" disabled={page <= 1} onClick={() => runPreview(page - 1)}>Prev</HrBtn>
                        <span>Page {pagination.page} / {pagination.total_pages} · {pagination.total_rows} rows</span>
                        <HrBtn variant="ghost" disabled={page >= pagination.total_pages} onClick={() => runPreview(page + 1)}>Next</HrBtn>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {tab === "exceptions" && (
              <div className="stat-ws__exceptions">
                {!data && <div className="stat-ws__empty"><p>Generate a preview to see exceptions.</p></div>}
                {data && exceptionEntries.length === 0 && (
                  <div className="stat-ws__ok"><ShieldCheck size={18} /> No exceptions detected.</div>
                )}
                {exceptionEntries.map(([kind, items]) => (
                  <div key={kind} className="stat-ws__exc-block">
                    <h4>{titleCase(kind)} <span>({items.length})</span></h4>
                    {items.length > 0 && (
                      <ul>
                        {items.slice(0, 50).map((it, i) => (
                          <li key={i}>{Object.entries(it).map(([k, v]) => `${k}: ${v}`).join(" · ")}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}

            {tab === "history" && (
              <HistoryPanel history={history} onRefresh={loadHistory} />
            )}

            {tab === "schedules" && (
              <SchedulePanel
                schedules={schedules}
                cadences={cadences}
                reportKey={selectedKey}
                reportName={selected?.name}
                onRefresh={loadSchedules}
              />
            )}

            {tab === "compliance" && <CompliancePanel score={compliance} onRefresh={loadCompliance} />}
          </section>
        </main>
        </div>
      </div>
    </ReportsEnterpriseShell>
    </div>
  );
}

function HistoryPanel({ history, onRefresh }) {
  const download = async (id, name) => {
    try {
      const res = await downloadArtifact(id);
      triggerBlobDownload(res, name);
    } catch (_) {} // eslint-disable-line no-empty
  };
  return (
    <div className="stat-ws__history">
      <div className="stat-ws__panel-head">
        <h3><HistoryIcon size={15} /> Download History</h3>
        <HrBtn variant="ghost" onClick={onRefresh}><RefreshCw size={14} /> Refresh</HrBtn>
      </div>
      <table className="stat-ws__grid">
        <thead><tr><th>Report</th><th>Period</th><th>Format</th><th className="num">Ver</th><th className="num">Size</th><th>By</th><th>When</th><th></th></tr></thead>
        <tbody>
          {history.map((h) => (
            <tr key={h.artifact_id}>
              <td>{h.report_name || h.report_key}</td>
              <td>{h.period_label}</td>
              <td>{(h.fmt || "").toUpperCase()}</td>
              <td className="num">{h.version}</td>
              <td className="num">{Math.round((h.byte_size || 0) / 1024)} KB</td>
              <td>{h.generated_by_label || "—"}</td>
              <td>{h.created_at ? new Date(h.created_at).toLocaleString() : ""}</td>
              <td><button className="stat-ws__link" onClick={() => download(h.artifact_id, h.file_name)}><Download size={14} /></button></td>
            </tr>
          ))}
          {history.length === 0 && <tr><td colSpan={8} className="stat-ws__norows">No downloads yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function SchedulePanel({ schedules, cadences, reportKey, reportName, onRefresh }) {
  const [name, setName] = useState("");
  const [cadence, setCadence] = useState(cadences[0] || "monthly");
  const [recipients, setRecipients] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await createSchedule({
        name: name || `${reportName} (${cadence})`,
        report_key: reportKey,
        cadence,
        format: "xlsx",
        params: {},
        recipients: recipients.split(",").map((s) => s.trim()).filter(Boolean),
      });
      setName("");
      setRecipients("");
      onRefresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stat-ws__schedules">
      <div className="stat-ws__sched-form">
        <h3><CalendarDays size={15} /> New schedule for {reportName}</h3>
        <div className="stat-ws__sched-row">
          <HrInput placeholder="Schedule name" value={name} onChange={(e) => setName(e.target.value)} />
          <HrSelect value={cadence} onChange={(e) => setCadence(e.target.value)}>
            {cadences.map((c) => <option key={c} value={c}>{c}</option>)}
          </HrSelect>
          <HrInput placeholder="Emails (comma separated)" value={recipients} onChange={(e) => setRecipients(e.target.value)} />
          <HrBtn onClick={submit} disabled={busy || !reportKey}>Create</HrBtn>
        </div>
      </div>
      <table className="stat-ws__grid">
        <thead><tr><th>Name</th><th>Report</th><th>Cadence</th><th>Next run</th><th>Active</th><th></th></tr></thead>
        <tbody>
          {schedules.map((s) => (
            <tr key={s.schedule_id}>
              <td>{s.name}</td>
              <td>{s.report_key}</td>
              <td>{s.cadence}</td>
              <td>{s.next_run_at ? new Date(s.next_run_at).toLocaleDateString() : "—"}</td>
              <td>
                <button className="stat-ws__link" onClick={() => toggleSchedule(s.schedule_id, !s.active).then(onRefresh)}>
                  {s.active ? "Pause" : "Resume"}
                </button>
              </td>
              <td><button className="stat-ws__link danger" onClick={() => deleteSchedule(s.schedule_id).then(onRefresh)}>Delete</button></td>
            </tr>
          ))}
          {schedules.length === 0 && <tr><td colSpan={6} className="stat-ws__norows">No schedules.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function CompliancePanel({ score, onRefresh }) {
  if (!score) return <div className="stat-ws__empty"><HrBtn onClick={onRefresh}><Gauge size={15} /> Compute score</HrBtn></div>;
  const gradeColor = { A: "#15803d", B: "#0369a1", C: "#b45309", D: "#c2410c", E: "#b91c1c" }[score.grade] || "#334155";
  return (
    <div className="stat-ws__compliance">
      <div className="stat-ws__score" style={{ borderColor: gradeColor }}>
        <p className="stat-ws__score-num" style={{ color: gradeColor }}>{score.score}</p>
        <p className="stat-ws__score-grade" style={{ background: gradeColor }}>Grade {score.grade}</p>
        <p className="stat-ws__score-sub">{score.total_issues} issue(s) · {score.active_employees} active employees</p>
        <HrBtn variant="ghost" onClick={onRefresh}><RefreshCw size={13} /> Recompute</HrBtn>
      </div>
      <div className="stat-ws__factors">
        {(score.factors || []).map((f) => (
          <div key={f.factor} className="stat-ws__factor">
            <div className="stat-ws__factor-head">
              <span>{f.factor}</span>
              <span>{f.earned} / {f.weight}</span>
            </div>
            <div className="stat-ws__bar"><div style={{ width: `${(f.earned / f.weight) * 100}%` }} /></div>
            <p className="stat-ws__factor-detail">{f.detail}</p>
            {f.issues?.length > 0 && <p className="stat-ws__factor-issues">{f.issues.join(" · ")}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
